import React from 'react';
import { useState, useEffect, useRef, useCallback } from "react"
import { TextBox } from "@progress/kendo-react-inputs"
import { Popup } from "@progress/kendo-react-popup"
import './TextBoxWithSuggestion.scss'

export default function TextBoxWithSuggestions({
  id,
  name,
  placeholder,
  value,
  onChange,
  suggestions = [],
  suggestionText = "Click for suggestions",
}) {
  const [showPopup, setShowPopup] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const popupRef = useRef(null)
  const buttonRef = useRef(null)

  // Close popup function
  const closePopup = useCallback(() => {
    setShowPopup(false)
  }, [])

  // Handle clicks anywhere on the document
  useEffect(() => {
    const handleDocumentClick = (event) => {
      const target = event.target

      // Don't close if clicking on the button
      if (buttonRef.current && buttonRef.current.contains(target)) {
        return
      }

      // Don't close if clicking inside the popup
      if (popupRef.current && popupRef.current.contains(target)) {
        return
      }

      // Close popup for any other click
      if (showPopup) {
        closePopup()
      }
    }

    // Handle escape key
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showPopup) {
        closePopup()
      }
    }

    if (showPopup) {
      // Use setTimeout to avoid immediate closing when opening
      setTimeout(() => {
        document.addEventListener("click", handleDocumentClick)
        document.addEventListener("keydown", handleKeyDown)
      }, 0)
    }

    return () => {
      document.removeEventListener("click", handleDocumentClick)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [showPopup, closePopup])

  const handleSuggestionClick = (suggestion) => {
    const syntheticEvent = {
      target: { name, value: suggestion },
      syntheticEvent: true,
    }
    onChange(syntheticEvent)
    closePopup()
  }

  const togglePopup = (e) => {
    e.stopPropagation()
    setShowPopup(!showPopup)
  }

  const handleCloseButtonClick = (e) => {
    e.stopPropagation()
    closePopup()
  }

  return (
    <div className="suggestion-input-container">
      <div className="input-wrapper">
        <TextBox
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="suggestion-textbox"
          
        />

        <button
          ref={(el) => {
            setAnchor(el)
            if (buttonRef.current !== el) {
              buttonRef.current = el
            }
          }}
          onClick={togglePopup}
          className={`suggestion-button ${showPopup ? "active" : ""}`}
          title={suggestionText}
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <circle cx="12" cy="17" r="0.5" fill="currentColor" />
          </svg>
        </button>
      </div>

      {suggestions.length > 0 && showPopup && (
        <Popup
          anchor={anchor}
          show={showPopup}
          anchorAlign={{ horizontal: "right", vertical: "bottom" }}
          popupAlign={{ horizontal: "right", vertical: "top" }}
          margin={{ horizontal: 0, vertical: 8 }}
        >
          <div ref={popupRef} className="suggestions-popup" onClick={(e) => e.stopPropagation()}>
            <div className="suggestions-header">
              <span>💡 Suggestions</span>
              <button onClick={handleCloseButtonClick} className="close-button" type="button">
                ×
              </button>
            </div>
            {suggestionText && <div className="suggestion-description">{suggestionText}</div>}
            <div className="suggestions-list">
              {suggestions.map((suggestion, index) => (
                <div key={index} onClick={() => handleSuggestionClick(suggestion)} className="suggestion-item">
                  <span className="suggestion-text">{suggestion}</span>
                  <span className="suggestion-arrow">→</span>
                </div>
              ))}
            </div>
          </div>
        </Popup>
      )}
    </div>
  )
}
